/**
 * EsocialNotificationsSection — Gerenciamento de destinatários de email
 * para notificações automáticas de rejeição de eventos eSocial pela gov.br.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Bell, Mail, Plus, Trash2, Send, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const EsocialNotificationsSection = ({ companyId }) => {
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);

  const reload = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/esocial/notif-emails/${companyId}`, auth());
      setEmails(r.data.emails || []);
    } catch (e) {
      toast.error('Erro ao carregar destinatários');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { reload(); }, [reload]);

  const save = async (next) => {
    try {
      await axios.put(`${API}/api/esocial/notif-emails/${companyId}`,
                      { emails: next }, auth());
      setEmails(next);
      toast.success('Destinatários atualizados');
    } catch (e) {
      toast.error('Erro ao salvar destinatários');
    }
  };

  const add = () => {
    const e = newEmail.trim().toLowerCase();
    if (!e || !e.includes('@')) {
      toast.error('Email inválido');
      return;
    }
    if (emails.includes(e)) {
      toast.warning('Email já adicionado');
      return;
    }
    save([...emails, e]);
    setNewEmail('');
  };

  const remove = (e) => save(emails.filter((x) => x !== e));

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await axios.post(
        `${API}/api/esocial/notif-test/${companyId}`, {}, auth(),
      );
      toast.success(`Email de teste enviado para ${r.data.sent.length} destinatário(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao enviar email de teste');
    } finally {
      setTesting(false);
    }
  };

  if (!companyId) return null;

  return (
    <Card className="border-amber-100 bg-gradient-to-br from-amber-50/30 to-white"
          data-testid="esocial-notif-section">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <h3 className="font-semibold text-base text-slate-900 flex items-center gap-2">
              <Bell className="h-4 w-4 text-amber-600" />
              Notificações de Rejeição (eSocial)
            </h3>
            <p className="text-xs text-slate-500 mt-0.5 max-w-2xl">
              Email automático para a equipe contábil quando um evento eSocial for rejeitado
              pela gov.br. Os destinatários abaixo recebem o detalhe das ocorrências e o link
              do evento. Se nenhum destinatário estiver cadastrado, todos os admins são notificados.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={sendTest} disabled={testing}
                  data-testid="notif-test-btn">
            {testing
              ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Enviando…</>
              : <><Send className="h-3.5 w-3.5 mr-1.5" />Enviar teste</>}
          </Button>
        </div>

        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider text-slate-500">
            Destinatários ({emails.length})
          </Label>
          <div className="flex flex-wrap gap-1.5">
            {emails.length === 0 && !loading && (
              <span className="text-xs text-slate-400 italic">
                Nenhum destinatário específico — notificações vão para todos os admins.
              </span>
            )}
            {emails.map((e) => (
              <Badge key={e} variant="secondary"
                     className="bg-slate-100 hover:bg-slate-200 border-slate-200 text-slate-700 pl-2.5 pr-1 py-1 gap-1">
                <Mail className="h-3 w-3 text-amber-600" />
                <span className="text-xs">{e}</span>
                <button
                  type="button"
                  onClick={() => remove(e)}
                  className="ml-0.5 p-0.5 rounded hover:bg-red-100 hover:text-red-600"
                  data-testid={`notif-remove-${e}`}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        </div>

        <div className="flex items-end gap-2 pt-1">
          <div className="flex-1">
            <Label className="text-xs">Adicionar email</Label>
            <Input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="contador@empresa.com"
              onKeyDown={(e) => e.key === 'Enter' && add()}
              data-testid="notif-email-input"
            />
          </div>
          <Button onClick={add} data-testid="notif-add-btn">
            <Plus className="h-4 w-4 mr-1.5" />Adicionar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default EsocialNotificationsSection;
